/**
 * Shared debounced search state hook for mobile scouting lists.
 *
 * Manages both the immediate display text and a debounced lowercase
 * search string suitable for query variables.
 * @module features/scouting/hooks/useDebouncedSearch
 */

import { useCallback, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

/** Search debounce delay -- 500ms gives enough time to type multi-word queries */
const SEARCH_DEBOUNCE_MS = 500;

/** Return value of useDebouncedSearch */
interface DebouncedSearchState {
  /** Current search input text (displayed in the input field) */
  readonly searchText: string;
  /** Debounced, lowercased, trimmed search string for query variables */
  readonly debouncedSearch: string;
  /** Update handler for the search input */
  readonly handleSearchChange: (text: string) => void;
  /** Clear handler that cancels pending debounce and resets both values */
  readonly handleClearSearch: () => void;
}

/**
 * Provides debounced search state for mobile scouting lists.
 *
 * The `searchText` value updates immediately for responsive input display.
 * The `debouncedSearch` value updates after 500ms of inactivity, lowercased
 * and trimmed, for use in query variables.
 * @returns Search state and handlers
 */
function useDebouncedSearch(): DebouncedSearchState {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debouncedSetSearch = useDebouncedCallback(
    setDebouncedSearch,
    SEARCH_DEBOUNCE_MS
  );

  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchText(text);
      debouncedSetSearch(text.toLowerCase().trim());
    },
    [debouncedSetSearch]
  );

  const handleClearSearch = useCallback(() => {
    debouncedSetSearch.cancel();
    setSearchText("");
    setDebouncedSearch("");
  }, [debouncedSetSearch]);

  return { searchText, debouncedSearch, handleSearchChange, handleClearSearch };
}

export default useDebouncedSearch;
